import { useState, useEffect, useCallback, useRef } from "react";
import {
  Accessibility, Plus, Minus, X, RotateCcw, Type, Contrast, Space,
  Volume2, VolumeX, Pause, Play, Square, Link2, MousePointer, Eye, BookOpen,
  Focus, Palette, AlignCenter, ImageOff
} from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "a11y-settings";

type A11ySettings = {
  fontSize: number;
  highContrast: boolean;
  wideSpacing: boolean;
  dyslexiaFont: boolean;
  underlineLinks: boolean;
  largeCursor: boolean;
  pauseAnimations: boolean;
  showAltText: boolean;
  focusHighlight: boolean;
  desaturate: boolean;
  readingGuide: boolean;
  hideImages: boolean;
};

const defaults: A11ySettings = {
  fontSize: 0,
  highContrast: false,
  wideSpacing: false,
  dyslexiaFont: false,
  underlineLinks: false,
  largeCursor: false,
  pauseAnimations: false,
  showAltText: false,
  focusHighlight: false,
  desaturate: false,
  readingGuide: false,
  hideImages: false,
};

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<A11ySettings>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
    } catch {
      return defaults;
    }
  });

  // TTS state
  const [isReading, setIsReading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const currentElRef = useRef<Element | null>(null);
  const elementsRef = useRef<Element[]>([]);
  const indexRef = useRef(0);

  // Reading guide state
  const [guideY, setGuideY] = useState(0);

  const apply = useCallback((s: A11ySettings) => {
    const root = document.documentElement;
    const scale = 100 + s.fontSize * 10;
    root.style.fontSize = s.fontSize === 0 ? "" : `${scale}%`;
    root.classList.toggle("a11y-high-contrast", s.highContrast);
    root.classList.toggle("a11y-wide-spacing", s.wideSpacing);
    root.classList.toggle("a11y-dyslexia-font", s.dyslexiaFont);
    root.classList.toggle("a11y-underline-links", s.underlineLinks);
    root.classList.toggle("a11y-large-cursor", s.largeCursor);
    root.classList.toggle("a11y-pause-animations", s.pauseAnimations);
    root.classList.toggle("a11y-show-alt", s.showAltText);
    root.classList.toggle("a11y-focus-highlight", s.focusHighlight);
    root.classList.toggle("a11y-desaturate", s.desaturate);
    root.classList.toggle("a11y-hide-images", s.hideImages);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  }, []);

  useEffect(() => { apply(settings); }, [settings, apply]);

  // Load dyslexia font when needed
  useEffect(() => {
    if (settings.dyslexiaFont) {
      const id = "opendyslexic-font";
      if (!document.getElementById(id)) {
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href = "https://fonts.cdnfonts.com/css/opendyslexic";
        document.head.appendChild(link);
      }
    }
  }, [settings.dyslexiaFont]);

  // Reading guide mouse tracking
  useEffect(() => {
    if (!settings.readingGuide) return;
    const handler = (e: MouseEvent) => setGuideY(e.clientY);
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, [settings.readingGuide]);

  // Alt+A keyboard shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === "a") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const update = (partial: Partial<A11ySettings>) => {
    setSettings((prev) => ({ ...prev, ...partial }));
  };

  const reset = () => setSettings(defaults);

  const isModified = Object.keys(defaults).some(
    (k) => settings[k as keyof A11ySettings] !== defaults[k as keyof A11ySettings]
  );

  // ── Full-Page TTS ──
  const stopReading = useCallback(() => {
    window.speechSynthesis.cancel();
    if (currentElRef.current) {
      currentElRef.current.classList.remove("a11y-reading-highlight");
    }
    setIsReading(false);
    setIsPaused(false);
    indexRef.current = 0;
  }, []);

  const readNext = useCallback(() => {
    const els = elementsRef.current;
    const idx = indexRef.current;
    if (idx >= els.length) {
      stopReading();
      return;
    }
    if (currentElRef.current) {
      currentElRef.current.classList.remove("a11y-reading-highlight");
    }
    const el = els[idx];
    el.classList.add("a11y-reading-highlight");
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    currentElRef.current = el;
    const text = el.textContent?.trim();
    if (!text) {
      indexRef.current++;
      readNext();
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.onend = () => { indexRef.current++; readNext(); };
    utterance.onerror = () => { indexRef.current++; readNext(); };
    window.speechSynthesis.speak(utterance);
  }, [stopReading]);

  const startReading = useCallback(() => {
    const main = document.getElementById("main-content") || document.querySelector("main");
    if (!main) return;
    const els = Array.from(
      main.querySelectorAll("h1, h2, h3, h4, p, li, figcaption, blockquote, td, th")
    ).filter((el) => {
      const text = el.textContent?.trim();
      return text && text.length > 0 && el.closest("[aria-hidden='true']") === null;
    });
    if (els.length === 0) return;
    elementsRef.current = els;
    indexRef.current = 0;
    setIsReading(true);
    setIsPaused(false);
    readNext();
  }, [readNext]);

  const togglePause = useCallback(() => {
    if (isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    } else {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  }, [isPaused]);

  useEffect(() => {
    return () => { window.speechSynthesis.cancel(); };
  }, []);

  const ToggleButton = ({ active, onToggle, icon: Icon, label, description }: {
    active: boolean; onToggle: () => void; icon: React.ElementType; label: string; description: string;
  }) => (
    <button
      onClick={onToggle}
      className={`w-full flex items-center gap-3 p-3 rounded-xl mb-2 border transition-colors text-left ${
        active
          ? "bg-primary/10 border-primary text-foreground"
          : "bg-transparent border-border text-muted-foreground hover:border-foreground/30"
      }`}
      aria-pressed={active}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <div>
        <span className="text-xs font-heading uppercase tracking-wide block">{label}</span>
        <span className="text-[10px] font-body">{description}</span>
      </div>
    </button>
  );

  return (
    <>
      {/* Reading guide overlay */}
      {settings.readingGuide && (
        <div
          className="fixed inset-x-0 z-[55] pointer-events-none"
          style={{ top: guideY - 20, height: 40 }}
          aria-hidden="true"
        >
          <div className="w-full h-full bg-primary/15 border-y border-primary/30" />
        </div>
      )}

      {/* Sticky TTS bar */}
      {isReading && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-primary text-primary-foreground shadow-lg">
          <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-2">
              <Volume2 className="h-4 w-4" />
              <span className="text-xs font-heading uppercase tracking-wide">Reading page aloud…</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="icon" variant="ghost" className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20" onClick={togglePause} aria-label={isPaused ? "Resume reading" : "Pause reading"}>
                {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20" onClick={stopReading} aria-label="Stop reading">
                <Square className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(!open)}
        aria-label="Accessibility options (Alt+A)"
        className="fixed bottom-[100px] left-6 z-[110] h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        <Accessibility className="h-5 w-5" />
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-[130px] left-6 z-[110] w-72 bg-card border border-border rounded-2xl shadow-2xl p-5 animate-in slide-in-from-bottom-4 fade-in duration-200 max-h-[65vh] overflow-y-auto"
          role="dialog"
          aria-label="Accessibility settings"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-sm font-bold uppercase tracking-wide flex items-center gap-1.5">
              <Accessibility className="h-4 w-4 text-primary" /> Accessibility
            </h2>
            <button onClick={() => setOpen(false)} aria-label="Close accessibility panel" className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Read Page Aloud */}
          <button
            onClick={() => { setOpen(false); isReading ? stopReading() : startReading(); }}
            className={`w-full flex items-center gap-3 p-3 rounded-xl mb-3 border transition-colors text-left ${
              isReading
                ? "bg-destructive/10 border-destructive text-foreground"
                : "bg-primary/10 border-primary text-foreground"
            }`}
          >
            {isReading ? <VolumeX className="h-4 w-4 shrink-0" /> : <BookOpen className="h-4 w-4 shrink-0" />}
            <div>
              <span className="text-xs font-heading uppercase tracking-wide block">
                {isReading ? "Stop Reading" : "Read Page Aloud"}
              </span>
              <span className="text-[10px] font-body">
                {isReading ? "Stop text-to-speech" : "Listen to the full page content"}
              </span>
            </div>
          </button>

          {/* Font Size */}
          <div className="mb-3">
            <label className="text-xs font-heading uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-2">
              <Type className="h-3.5 w-3.5" /> Text Size
            </label>
            <div className="flex items-center gap-2">
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => update({ fontSize: Math.max(-1, settings.fontSize - 1) })} disabled={settings.fontSize <= -1} aria-label="Decrease text size">
                <Minus className="h-3.5 w-3.5" />
              </Button>
              <span className="text-sm font-body flex-1 text-center">
                {settings.fontSize === 0 ? "Default" : `${settings.fontSize > 0 ? "+" : ""}${settings.fontSize * 10}%`}
              </span>
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => update({ fontSize: Math.min(3, settings.fontSize + 1) })} disabled={settings.fontSize >= 3} aria-label="Increase text size">
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Toggles */}
          <ToggleButton active={settings.highContrast} onToggle={() => update({ highContrast: !settings.highContrast })} icon={Contrast} label="High Contrast" description="Increase text contrast" />
          <ToggleButton active={settings.focusHighlight} onToggle={() => update({ focusHighlight: !settings.focusHighlight })} icon={Focus} label="Focus Highlight" description="Bold outline on focused elements" />
          <ToggleButton active={settings.desaturate} onToggle={() => update({ desaturate: !settings.desaturate })} icon={Palette} label="Grayscale" description="Remove colors from the page" />
          <ToggleButton active={settings.readingGuide} onToggle={() => update({ readingGuide: !settings.readingGuide })} icon={AlignCenter} label="Reading Guide" description="Line that follows your cursor" />
          <ToggleButton active={settings.wideSpacing} onToggle={() => update({ wideSpacing: !settings.wideSpacing })} icon={Space} label="Wide Spacing" description="Increase letter & line spacing" />
          <ToggleButton active={settings.dyslexiaFont} onToggle={() => update({ dyslexiaFont: !settings.dyslexiaFont })} icon={Type} label="Dyslexia Font" description="Switch to OpenDyslexic font" />
          <ToggleButton active={settings.underlineLinks} onToggle={() => update({ underlineLinks: !settings.underlineLinks })} icon={Link2} label="Underline Links" description="Make all links underlined" />
          <ToggleButton active={settings.largeCursor} onToggle={() => update({ largeCursor: !settings.largeCursor })} icon={MousePointer} label="Large Cursor" description="Enlarge the mouse cursor" />
          <ToggleButton active={settings.pauseAnimations} onToggle={() => update({ pauseAnimations: !settings.pauseAnimations })} icon={Pause} label="Pause Animations" description="Stop all animations & transitions" />
          <ToggleButton active={settings.hideImages} onToggle={() => update({ hideImages: !settings.hideImages })} icon={ImageOff} label="Hide Images" description="Reduce visual noise from images" />
          <ToggleButton active={settings.showAltText} onToggle={() => update({ showAltText: !settings.showAltText })} icon={Eye} label="Image Descriptions" description="Show alt text below images" />

          {/* Reset */}
          {isModified && (
            <Button variant="ghost" size="sm" className="w-full text-xs font-heading uppercase tracking-wide mt-2" onClick={reset}>
              <RotateCcw className="h-3 w-3 mr-1" /> Reset to Defaults
            </Button>
          )}

          <p className="text-[9px] text-muted-foreground text-center mt-3">Press Alt+A to toggle this panel</p>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;
